import { BracketNode } from "model/Bracket/BracketGraph";
import {
  depthToFullTitleDoubleElim,
  depthToFullTitleSingleElim,
} from "model/Bracket/BracketTitles";
import { pushX, unshiftX } from "Utility";
import { BracketMatchArgs } from "./BracketMatchArgs";
import { BracketRowMatch, BracketRowMatchPlaceholder } from "./BracketRowMatch";
import { createBracketLine } from "./Lines/BracketLines";
import {
  MatchSizedGap,
  NodeColumn,
  BracketRoundHeader,
  GapColumn,
  CenterLineColumn,
} from "./styles";
import { JSX } from "react";

export const mapRowWithBracketLines = (
  bracketMatchArgs: BracketMatchArgs | undefined,
  tournamentId: string,
  nodesByDepth: (BracketNode | undefined)[][],
  spaceColumns: number[],
  maxDepth: number,
  isLower: boolean,
  isDoubleElim: boolean,
  compact: boolean,
  padding: number = 0
) => {
  const nodeCells: JSX.Element[] = [];
  const titleCells: JSX.Element[] = [];
  const maxNumberOfNodes = nodesByDepth.at(-1)?.length ?? 0;
  const hasMultipleFinals = nodesByDepth[0].length > 1;
  for (var i = 0; i < nodesByDepth.length; i++) {
    const currentColumn = nodesByDepth[i];
    const numberOfNodes = currentColumn.length;
    const nextColumn =
      i < nodesByDepth.length - 1 ? nodesByDepth[i + 1] : undefined;

    // Create the node column for the current depth
    const nodeColumnContents: JSX.Element[] = [];
    currentColumn.forEach((n, j) => {
      if (!n) {
        nodeColumnContents.push(<BracketRowMatchPlaceholder />);
      } else {
        nodeColumnContents.push(
          <BracketRowMatch
            key={n.match.matchId}
            node={n}
            tournamentId={tournamentId}
            args={bracketMatchArgs}
          />
        );
      }

      if (numberOfNodes < maxNumberOfNodes && j < numberOfNodes - 1) {
        pushX(
          <MatchSizedGap />,
          maxNumberOfNodes / numberOfNodes - 1,
          nodeColumnContents
        );
      }
    });
    nodeCells.unshift(<NodeColumn>{nodeColumnContents}</NodeColumn>);

    // Create the title for the current depth
    titleCells.unshift(
      <NodeColumn>
        <BracketRoundHeader isLower={isLower}>
          {isDoubleElim
            ? depthToFullTitleDoubleElim(
                i + 1,
                maxDepth,
                isLower,
                hasMultipleFinals
              )
            : depthToFullTitleSingleElim(i + 1, maxDepth, hasMultipleFinals)}
        </BracketRoundHeader>
      </NodeColumn>
    );

    // If there are more columns, render appropriate bracket lines
    if (!!nextColumn) {
      const nodeCellContents: JSX.Element[] = [];
      const isSpaceColumn = spaceColumns.includes(i + 1);
      currentColumn.forEach((n) => {
        nodeCellContents.push(createBracketLine(n, isSpaceColumn));
      });

      if (isSpaceColumn) {
        nodeCells.unshift(
          <GapColumn compact={compact}>{nodeCellContents}</GapColumn>
        );
        titleCells.unshift(<GapColumn compact={compact} />);
      } else {
        nodeCells.unshift(
          <CenterLineColumn compact={compact}>
            {nodeCellContents}
          </CenterLineColumn>
        );
        titleCells.unshift(<CenterLineColumn compact={compact} />);
      }
    }
  }

  unshiftX(
    <>
      <CenterLineColumn compact={compact} />
      <NodeColumn />
    </>,
    padding,
    nodeCells
  );
  unshiftX(
    <>
      <CenterLineColumn compact={compact} />
      <NodeColumn />
    </>,
    padding,
    titleCells
  );
  return { nodeCells, titleCells };
};

export const getPaddedRow = (padding: number, compact: boolean) => {
  const nodeCells: JSX.Element[] = [];
  const titleCells: JSX.Element[] = [];

  for (var i = 0; i < padding; i++) {
    if (i % 2 === 0) {
      nodeCells.unshift(<NodeColumn />);
      titleCells.unshift(<NodeColumn />);
    } else {
      nodeCells.unshift(<CenterLineColumn compact={compact} />);
      titleCells.unshift(<CenterLineColumn compact={compact} />);
    }
  }

  return { nodeCells, titleCells };
};
